import { useEffect, useState } from 'react';
import { createFileRoute, Outlet, redirect } from '@tanstack/react-router';
import * as Sentry from '@sentry/react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { AppLayout } from '@/components/layout/AppLayout';
import { Button } from '@/components/ui/Button';
import { WelcomeScreen, hasCompletedOnboarding } from '@/components/onboarding';

// Issue #123: scope render errors to the _app subtree so a bug in one route
// doesn't blank the whole application (login + route root still render).
function AppRouteErrorBoundary({ error }: { error: Error }) {
  useEffect(() => {
    Sentry.captureException(error, { tags: { scope: 'route:_app' } });
  }, [error]);
  return (
    <AppLayout farmGate={false}>
      <div className="max-w-md mx-auto mt-24 text-center p-8">
        <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
        <h1 className="text-xl font-semibold text-gray-900">Something went wrong on this page</h1>
        <p className="text-sm text-gray-600 mt-2">
          The rest of the app is fine. Try refreshing, or navigate elsewhere from the sidebar.
        </p>
        <Button
          className="mt-6"
          variant="primary"
          leftIcon={<RefreshCw size={16} />}
          onClick={() => window.location.reload()}
        >
          Refresh
        </Button>
      </div>
    </AppLayout>
  );
}

export const Route = createFileRoute('/_app')({
  beforeLoad: async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      throw redirect({ to: '/login' });
    }
    return { session };
  },
  component: AppLayoutWrapper,
  errorComponent: AppRouteErrorBoundary,
});

function AppLayoutWrapper() {
  const [showOnboarding, setShowOnboarding] = useState(() => !hasCompletedOnboarding());

  // Issue #110: subscribe to the browser 'storage' event so if the user
  // completes onboarding in another tab, this tab sees it and drops the
  // WelcomeScreen on the next storage broadcast (within the session).
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      // hasCompletedOnboarding() reads localStorage under the hood; recheck
      // whenever anything changes.
      if (!e.key || e.key.includes('onboarding')) {
        setShowOnboarding(!hasCompletedOnboarding());
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  if (showOnboarding) {
    return <WelcomeScreen onComplete={() => setShowOnboarding(false)} />;
  }

  return (
    <AppLayout>
      <Outlet />
    </AppLayout>
  );
}
